import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { forgotPassword } from '../services/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: 'Fout',
        description: 'Vul je e-mailadres in',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
      toast({
        title: 'E-mail verstuurd',
        description: 'Controleer je inbox voor de reset link',
      });
    } catch (error) {
      // Still show success to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a2332] via-[#2d3e50] to-[#1a2332] flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md shadow-2xl border-2 border-[#FDB913]">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-green-500 rounded-full p-4">
                <CheckCircle size={40} className="text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-4">E-mail Verstuurd!</h2>
            <p className="text-muted-foreground mb-6">
              Als dit e-mailadres bij ons bekend is, ontvang je binnen enkele minuten een link om je wachtwoord te herstellen.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Controleer ook je spam folder.
            </p>
            <Link to="/login">
              <Button className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-semibold">
                <ArrowLeft size={16} className="mr-2" />
                Terug naar Inloggen
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a2332] via-[#2d3e50] to-[#1a2332] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md shadow-2xl border-2 border-[#FDB913]">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-[#FDB913] rounded-full p-4">
              <Mail size={40} className="text-[#1a2332]" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">Wachtwoord Vergeten</CardTitle>
          <CardDescription>Vul je e-mailadres in om een reset link te ontvangen</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="flex items-center space-x-2 mb-2">
                <Mail size={16} />
                <span>E-mailadres</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Vul je e-mailadres in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-gray-300 focus:border-[#FDB913] focus:ring-[#FDB913]"
                data-testid="forgot-password-email"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-semibold py-6"
              disabled={loading}
              data-testid="forgot-password-submit"
            >
              {loading ? 'Versturen...' : 'Reset Link Versturen'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-[#FDB913] hover:text-[#e5a911] font-medium flex items-center justify-center space-x-2"
            >
              <ArrowLeft size={16} />
              <span>Terug naar Inloggen</span>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
